import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const MidtermExamPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student;

  // === Core Data States ===
  const [availableExams, setAvailableExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // === UI States ===
  const [showInstructions, setShowInstructions] = useState(true);
  const [showReview, setShowReview] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [examStarted, setExamStarted] = useState(false);
  const [warningShown, setWarningShown] = useState(false);

  // === Auto-save States ===
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveInterval, setAutoSaveInterval] = useState(null);

  // === Refs ===
  const timerRef = useRef(null);
  const autoSaveRef = useRef(null);

  // ========================= INITIALIZATION =========================
  useEffect(() => {
    if (!student) {
      navigate('/student-login');
      return;
    }

    loadAvailableExams();
  }, [student, navigate]);

  useEffect(() => {
    if (selectedExam && examStarted && timeRemaining !== null) {
      startTimer();
      startAutoSave();
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoSaveRef.current) clearInterval(autoSaveRef.current);
    };
  }, [selectedExam, examStarted, timeRemaining]);

  // ========================= DATA FETCHING =========================
  const loadAvailableExams = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/exams/Midterm`);

      if (!response.ok) {
        throw new Error('Failed to load midterm exams');
      }

      const exams = await response.json();

      // Filter exams for this student's class and subject
      const studentExams = exams.filter(exam =>
        exam.className === student.className
      );

      setAvailableExams(studentExams);

      if (studentExams.length === 0) {
        setError('No midterm exams are currently available for your class.');
      }
    } catch (err) {
      setError('Failed to load midterm exams. Please try again.');
      console.error('Error loading exams:', err);
    } finally {
      setLoading(false);
    }
  };

  // ========================= TIMER MANAGEMENT =========================
  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          // Time's up - auto-submit
          handleAutoSubmit();
          return 0;
        }

        // Show warning at 5 minutes remaining
        if (prev === 300 && !warningShown) {
          setWarningShown(true);
          alert('Warning: 5 minutes remaining!');
        }

        return prev - 1;
      });
    }, 1000);
  };

  const formatTime = (seconds) => {
    // Handle invalid time values
    if (!seconds || isNaN(seconds) || seconds < 0) {
      return '00:00';
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // ========================= AUTO-SAVE MANAGEMENT =========================
  const startAutoSave = () => {
    if (autoSaveRef.current) clearInterval(autoSaveRef.current);

    autoSaveRef.current = setInterval(() => {
      saveProgress();
    }, 30000); // Auto-save every 30 seconds
  };

  const saveProgress = async () => {
    if (!selectedExam || Object.keys(answers).length === 0) return;

    try {
      // Save to localStorage as backup
      const progressData = {
        examId: selectedExam.id,
        answers,
        currentQuestionIndex,
        timestamp: Date.now()
      };
      localStorage.setItem(`midterm_progress_${student.username}_${selectedExam.id}`, JSON.stringify(progressData));

      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  };

  // ========================= ANSWER HANDLING =========================
  const handleAnswerChange = useCallback((questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  }, []);

  const handleTextAnswerChange = useCallback((questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  }, []);

  // ========================= NAVIGATION =========================
  const goToQuestion = (index) => {
    if (index >= 0 && index < selectedExam.questions.length) {
      setCurrentQuestionIndex(index);
    }
  };

  const goToNext = () => {
    if (currentQuestionIndex < selectedExam.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const goToPrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  // ========================= EXAM MANAGEMENT =========================
  const startExam = (exam) => {
    setSelectedExam(exam);
    setTimeRemaining(exam.duration * 60); // Convert minutes to seconds
    setShowInstructions(true);
    setExamStarted(false);

    // Load any saved progress
    const savedProgress = localStorage.getItem(`midterm_progress_${student.username}_${exam.id}`);
    if (savedProgress) {
      try {
        const progress = JSON.parse(savedProgress);
        if (progress.examId === exam.id) {
          setAnswers(progress.answers || {});
          setCurrentQuestionIndex(progress.currentQuestionIndex || 0);
          alert('Previous progress loaded. You can continue where you left off.');
        }
      } catch (err) {
        console.error('Error loading saved progress:', err);
      }
    }
  };

  const beginExam = () => {
    setShowInstructions(false);
    setExamStarted(true);
  };

  const showReviewMode = () => {
    setShowReview(true);
    saveProgress(); // Final save before review
  };

  const backToExam = () => {
    setShowReview(false);
  };

  // ========================= SUBMISSION =========================
  const handleAutoSubmit = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoSaveRef.current) clearInterval(autoSaveRef.current);

    alert('Time is up! Your exam has been auto-submitted.');

    await submitExam(true);
  };

  const handleSubmit = async () => {
    const unansweredCount = selectedExam.questions.length - Object.keys(answers).length;

    if (unansweredCount > 0) {
      if (!window.confirm(`You have ${unansweredCount} unanswered question(s). Are you sure you want to submit?`)) {
        return;
      }
    }

    if (!window.confirm('Are you sure you want to submit your midterm examLoading This action cannot be undone.')) {
      return;
    }

    await submitExam(false);
  };

  const submitExam = async (autoSubmitted = false) => {
    setSubmitting(true);

    try {
      // Calculate score (basic implementation - you might want to enhance this)
      let totalScore = 0;
      let maxScore = 0;

      selectedExam.questions.forEach(question => {
        maxScore += question.points || 1;
        const userAnswer = answers[question.questionId];
        if (userAnswer && userAnswer.toLowerCase().trim() === question.correctAnswer.toLowerCase().trim()) {
          totalScore += question.points || 1;
        }
      });

      const percentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
      const passed = percentage >= (selectedExam.passingScore || 50);

      const submissionData = {
        studentId: student.username,
        studentName: `${student.firstName} ${student.lastName}`,
        examId: selectedExam.id,
        examType: 'Midterm',
        examTitle: selectedExam.title,
        answers,
        score: totalScore,
        maxScore,
        percentage,
        passed,
        autoSubmitted,
        submittedAt: new Date().toISOString(),
        timeSpent: selectedExam.duration * 60 - timeRemaining // in seconds
      };

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/exams/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit exam');
      }

      const result = await response.json();

      // Clear saved progress
      localStorage.removeItem(`midterm_progress_${student.username}_${selectedExam.id}`);

      // Show results
      alert(`Exam submitted successfully!\n\nScore: ${totalScore}/${maxScore} (${percentage}%)\n${passed ? 'PASSED' : 'FAILED'}`);

      navigate('/student-dashboard', { state: { student } });
    } catch (error) {
      alert('Failed to submit exam: ' + error.message);
      setSubmitting(false);
    }
  };

  // ========================= RENDER HELPERS =========================
  const getQuestionStatus = (questionId) => {
    if (answers[questionId]) return 'answered';
    return 'unanswered';
  };

  const getQuestionTypeIcon = (type) => {
    switch (type) {
      case 'MCQ': return 'Note';
      case 'True/False': return '??';
      case 'Short Answer': return 'Note';
      case 'Essay': return 'Essay';
      default: return 'Help';
    }
  };

  const getAnsweredCount = () => {
    return Object.keys(answers).length;
  };

  const getProgressPercentage = () => {
    return selectedExam ? (getAnsweredCount() / selectedExam.questions.length) * 100 : 0;
  };

  // ========================= MAIN RENDER =========================
  if (!student) {
    navigate('/student-login');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading midterm exams...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow">
          <div className="text-6xl mb-4">Warning</div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Exam Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/student-dashboard', { state: { student } })}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Exam Selection Screen
  if (!selectedExam) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div>
                <h1 className="text-2xl font-bold text-blue-900">Midterm Examinations</h1>
                <p className="text-sm text-gray-600">Select and take your midterm exam</p>
              </div>
              <button
                onClick={() => navigate('/student-dashboard', { state: { student } })}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {availableExams.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">Note</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No Midterm Exams Available</h3>
              <p className="text-gray-600 mb-6">
                There are currently no midterm exams available for your class. Please check back later.
              </p>
              <button
                onClick={() => navigate('/student-dashboard', { state: { student } })}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
              >
                Back to Dashboard
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800">Available Midterm Exams</h2>
              <div className="grid gap-4">
                {availableExams.map((exam) => (
                  <div key={exam.id} className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900">{exam.title}</h3>
                        <p className="text-gray-600 mb-2">{exam.description}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                          <div><strong>Subject:</strong> {exam.subjectName}</div>
                          <div><strong>Duration:</strong> {exam.duration} min</div>
                          <div><strong>Questions:</strong> {exam.questions?.length || 0}</div>
                          <div><strong>Passing Score:</strong> {exam.passingScore || 50}%</div>
                        </div>
                        {exam.instructions && (
                          <div className="mt-3 p-3 bg-blue-50 rounded border-l-4 border-blue-400">
                            <strong>Instructions:</strong>
                            <p className="text-blue-800 mt-1">{exam.instructions}</p>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => startExam(exam)}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 ml-6"
                      >
                        Start Exam
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Exam Instructions Screen
  if (showInstructions && !examStarted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-xl">
          <div className="p-8">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">Note</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedExam.title}</h2>
              <p className="text-gray-600">Midterm Examination Instructions</p>
            </div>

            <div className="space-y-6 mb-8">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{selectedExam.questions.length}</div>
                  <div className="text-sm text-blue-800">Questions</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{selectedExam.duration}</div>
                  <div className="text-sm text-green-800">Minutes</div>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">Warning Important Notes:</h4>
                <ul className="text-yellow-700 text-sm space-y-1">
                  <li>? Once you start, the timer cannot be paused</li>
                  <li>? Your progress is auto-saved every 30 seconds</li>
                  <li>? Make sure you have a stable internet connection</li>
                  <li>? Do not refresh or close the page during the exam</li>
                </ul>
              </div>

              {selectedExam.instructions && (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Applications Exam Instructions:</h4>
                  <p className="text-blue-700">{selectedExam.instructions}</p>
                </div>
              )}
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setSelectedExam(null)}
                className="flex-1 bg-gray-600 text-white py-3 px-6 rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={beginExam}
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700"
              >
                Start Exam
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Review Mode
  if (showReview) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header with Timer */}
        <div className="bg-white shadow-sm border-b sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">{selectedExam.title}</h1>
                <p className="text-sm text-gray-600">Review your answers before submission</p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-red-600">{formatTime(timeRemaining)}</div>
                <div className="text-xs text-gray-500">Time Remaining</div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Exam Review</h2>
              <p className="text-gray-600">Check your answers before final submission</p>
            </div>

            <div className="p-6">
              {selectedExam.questions.map((question, index) => (
                <div key={question.questionId} className="border-b border-gray-200 pb-6 mb-6 last:border-b-0">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                        {index + 1}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-lg">{getQuestionTypeIcon(question.questionType)}</span>
                        <span
                          className="font-medium text-gray-900"
                          dangerouslySetInnerHTML={{ __html: question.questionText }}
                        />
                        <span className={`px-2 py-1 text-xs rounded ${
                          answers[question.questionId] ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {answers[question.questionId] ? 'Answered' : 'Unanswered'}
                        </span>
                      </div>

                      {question.questionType === 'MCQ' && question.options && (
                        <div className="ml-4 space-y-1">
                          {question.options.map((option, optIndex) => (
                            <div key={optIndex} className={`flex items-center space-x-2 p-2 rounded ${
                              answers[question.questionId] === option ? 'bg-blue-50 border border-blue-200' : ''
                            }`}>
                              <input
                                type="radio"
                                checked={answers[question.questionId] === option}
                                readOnly
                                className="text-blue-600"
                              />
                              <span className={answers[question.questionId] === option ? 'font-medium text-blue-800' : 'text-gray-700'}>
                                {String.fromCharCode(97 + optIndex)}) {option}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {question.questionType === 'True/False' && (
                        <div className="ml-4 space-y-1">
                          {['True', 'False'].map((option) => (
                            <div key={option} className={`flex items-center space-x-2 p-2 rounded ${
                              answers[question.questionId] === option ? 'bg-blue-50 border border-blue-200' : ''
                            }`}>
                              <input
                                type="radio"
                                checked={answers[question.questionId] === option}
                                readOnly
                                className="text-blue-600"
                              />
                              <span className={answers[question.questionId] === option ? 'font-medium text-blue-800' : 'text-gray-700'}>
                                {option}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {(question.questionType === 'Short Answer' || question.questionType === 'Essay') && (
                        <div className="ml-4">
                          <textarea
                            value={answers[question.questionId] || ''}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                            rows={question.questionType === 'Essay' ? 4 : 2}
                            placeholder="No answer provided"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 border-t bg-gray-50">
              <div className="flex justify-between items-center">
                <button
                  onClick={backToExam}
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700"
                >
                  Back to Exam
                </button>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">
                    {getAnsweredCount()} of {selectedExam.questions.length} Answered
                  </div>
                  <div className="text-sm text-gray-600">
                    {Math.round(getProgressPercentage())}% Complete
                  </div>
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Exam'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Exam Interface
  const currentQuestion = selectedExam.questions[currentQuestionIndex];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Timer and Progress */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-lg font-bold text-gray-900">{selectedExam.title}</h1>
              <p className="text-sm text-gray-600">Question {currentQuestionIndex + 1} of {selectedExam.questions.length}</p>
            </div>
            <div className="text-right">
              <div className={`text-xl font-bold ${timeRemaining < 300 ? 'text-red-600 animate-pulse' : 'text-blue-600'}`}>
                {formatTime(timeRemaining)}
              </div>
              <div className="text-xs text-gray-500">Time Remaining</div>
              {lastSaved && (
                <div className="text-xs text-green-600">Auto-saved: {lastSaved}</div>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${getProgressPercentage()}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-600 mt-1">
              <span>{getAnsweredCount()} answered</span>
              <span>{Math.round(getProgressPercentage())}% complete</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Question Navigation Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">Questions</h3>
              <div className="grid grid-cols-5 gap-2">
                {selectedExam.questions.map((question, index) => (
                  <button
                    key={question.questionId}
                    onClick={() => goToQuestion(index)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium border-2 transition-colors ${
                      index === currentQuestionIndex
                        ? 'bg-blue-600 text-white border-blue-600'
                        : getQuestionStatus(question.questionId) === 'answered'
                        ? 'bg-green-100 text-green-800 border-green-300'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t">
                <button
                  onClick={showReviewMode}
                  className="w-full bg-yellow-600 text-white py-2 px-4 rounded-lg hover:bg-yellow-700 transition duration-200"
                >
                  Review Answers
                </button>
              </div>
            </div>
          </div>

          {/* Main Question Area */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow">
              <div className="p-8">
                {/* Question Header */}
                <div className="flex items-center space-x-3 mb-6">
                  <span className="text-2xl">{getQuestionTypeIcon(currentQuestion.questionType)}</span>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-gray-900 mb-1">
                      Question {currentQuestionIndex + 1}
                    </h2>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>{currentQuestion.questionType}</span>
                      <span>?</span>
                      <span>{currentQuestion.points || 1} point(s)</span>
                    </div>
                  </div>
                </div>

                {/* Question Content */}
                <div className="mb-8">
                  <div
                    className="text-lg text-gray-800 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: currentQuestion.questionText }}
                  />
                </div>

                {/* Answer Input */}
                <div className="mb-8">
                  {currentQuestion.questionType === 'MCQ' && currentQuestion.options && (
                    <div className="space-y-3">
                      {currentQuestion.options.map((option, index) => (
                        <label key={index} className="flex items-center space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="radio"
                            name={`question-${currentQuestion.questionId}`}
                            value={option}
                            checked={answers[currentQuestion.questionId] === option}
                            onChange={() => handleAnswerChange(currentQuestion.questionId, option)}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-800">
                            <strong className="text-blue-600 mr-2">{String.fromCharCode(97 + index)})</strong>
                            {option}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  {currentQuestion.questionType === 'True/False' && (
                    <div className="space-y-3">
                      {['True', 'False'].map((option) => (
                        <label key={option} className="flex items-center space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="radio"
                            name={`question-${currentQuestion.questionId}`}
                            value={option}
                            checked={answers[currentQuestion.questionId] === option}
                            onChange={() => handleAnswerChange(currentQuestion.questionId, option)}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-800 font-medium">{option}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {currentQuestion.questionType === 'Short Answer' && (
                    <div>
                      <textarea
                        value={answers[currentQuestion.questionId] || ''}
                        onChange={(e) => handleTextAnswerChange(currentQuestion.questionId, e.target.value)}
                        placeholder="Enter your answer..."
                        rows="4"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  {currentQuestion.questionType === 'Essay' && (
                    <div>
                      <textarea
                        value={answers[currentQuestion.questionId] || ''}
                        onChange={(e) => handleTextAnswerChange(currentQuestion.questionId, e.target.value)}
                        placeholder="Write your essay answer here..."
                        rows="8"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-sm text-gray-500 mt-2">Provide a detailed, well-structured response</p>
                    </div>
                  )}
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between items-center pt-6 border-t">
                  <button
                    onClick={goToPrevious}
                    disabled={currentQuestionIndex === 0}
                    className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                  >
                    Previous
                  </button>

                  <div className="text-center">
                    <div className="text-sm text-gray-600">
                      Question {currentQuestionIndex + 1} of {selectedExam.questions.length}
                    </div>
                  </div>

                  {currentQuestionIndex === selectedExam.questions.length - 1 ? (
                    <button
                      onClick={showReviewMode}
                      className="px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition duration-200"
                    >
                      Review Exam
                    </button>
                  ) : (
                    <button
                      onClick={goToNext}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200"
                    >
                      Next
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MidtermExamPage;





